import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ARRAY_ERROR } from 'final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';

import FormNameContext from './FormNameContext';
import { buildMeta } from './Field';


// redux-form puts the error of the array itself into `_error`
// (errors.items = { _error: '...' }); final-form-arrays uses ARRAY_ERROR.
// Item errors (arrays) are not errors of the array field.
export const getArrayError = (error) => {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error[ARRAY_ERROR] || error._error;
  }
  if (typeof error === 'object') {
    return error._error || error[ARRAY_ERROR];
  }
  return undefined;
};

// `fields` object of redux-form (getAll, get, splice, removeAll, ...)
// built on top of final-form-arrays.
const buildFields = (rffFields) => {
  const getAll = () => (Array.isArray(rffFields.value) ? rffFields.value : []);
  return {
    name: rffFields.name,
    length: rffFields.length,
    map: rffFields.map,
    forEach: rffFields.forEach,
    push: value => rffFields.push(value),
    pop: () => rffFields.pop(),
    shift: () => rffFields.shift(),
    unshift: value => rffFields.unshift(value),
    insert: (index, value) => rffFields.insert(index, value),
    remove: index => rffFields.remove(index),
    move: (from, to) => rffFields.move(from, to),
    swap: (indexA, indexB) => rffFields.swap(indexA, indexB),
    update: (index, value) => rffFields.update(index, value),
    removeAll: () => rffFields.removeBatch(getAll().map((item, index) => index)),
    splice: (index, removeNum, ...values) => {
      if (removeNum === 1 && values.length === 1) {
        rffFields.update(index, values[0]);
        return;
      }
      for (let count = 0; count < removeNum; count += 1) {
        rffFields.remove(index);
      }
      values.forEach((value, offset) => rffFields.insert(index + offset, value));
    },
    get: index => getAll()[index],
    getAll,
  };
};

const FieldArray = ({ name, component: Component, ...rest }) => {
  const formName = useContext(FormNameContext);
  const dispatch = useDispatch();
  const { fields: rffFields, meta: rffMeta } = useFieldArray(name);
  const fields = buildFields(rffFields);
  const meta = {
    ...buildMeta(rffMeta, { form: formName, dispatch }),
    error: getArrayError(rffMeta.error),
  };
  return <Component {...rest} fields={fields} meta={meta} />;
};

FieldArray.propTypes = {
  name: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
};

export default FieldArray;
